import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import './ClarifyCard.css';

/**
 * 🃏 첫 턴 되묻기 카드
 *
 * mode A: 선택지 + 추천값(첫 칩, 미리 선택) + 20초 유휴 진행선. 카드 안에서 무엇이든 하면
 *         타이머가 멈추고(포인터·포커스·터치), 값을 바꾸면 처음부터 다시 잰다. 만료되면
 *         현재 값(=기본값)으로 자동 진행한다. (WCAG 2.2.1: 사용자 상호작용으로 연장·정지 가능)
 * mode B: 필수 정보 한 가지를 짧게 입력. 타이머 없음.
 * 두 모드 모두 "건너뛰기" 로 조건 없이 진행할 수 있다(Cursor AskQuestion 의 Skip 과 같은 역할).
 *
 * 부모는 onSubmit(answers, result) 로 [확인한 조건] 메시지를 만든다.
 *   answers: [{ key, label(질문 요약), value }]
 *   result: 'answered' | 'edited' | 'timer_expired' | 'skipped'
 */

const CUSTOM_VALUE = '__custom__';
const TICK_MS = 100;

export const CLARIFY_ANSWER_PREFIX = '[확인한 조건]';

/** 질문 문장 → 조건 라벨. "기후 변화 학습지는 어느 학년용인가요?" → "학년" 같은 짧은 라벨을 key 로 만든다. */
export const clarifyKeyLabel = (key) => {
  const table = {
    grade: '학년',
    school_level: '학교급',
    subject: '교과',
    item_type: '문항 유형',
    item_count: '문항 수',
    slide_count: '슬라이드 수',
    audience: '대상',
    purpose: '용도',
    tone: '문체',
    length: '분량',
    record_type: '기록 항목',
    channel: '전달 방식',
    topic: '주제',
    student_traits: '학생 특성',
    format: '형식',
    difficulty: '난이도',
    document_type: '문서 종류',
    duration: '시간',
  };
  if (table[key]) return table[key];
  const normalized = String(key || '').replace(/_/g, ' ').trim();
  return normalized || '조건';
};

const CLARIFY_LABEL_MAX = 80;
const CLARIFY_CHIP_LABEL_MAX = 24;

/**
 * 모델 펜스처럼 표에 없는 key(target 등)면 질문 문장을 라벨로 쓴다("어느 것을 줄일까요?" → "어느 것을 줄일까요").
 * 이 라벨은 [확인한 조건] 메시지로 모델에게 가므로 자르지 않는다(백엔드 parse_clarify_answer 도 80자).
 * 화면 칩에서는 clarifyChipLabel 로 짧게 보여 준다.
 */
export const clarifyQuestionLabel = (question) => {
  const byKey = clarifyKeyLabel(question?.key);
  if (byKey !== String(question?.key || '').replace(/_/g, ' ').trim() || !question?.text) return byKey;
  const text = String(question.text).replace(/[?？。.!]+$/g, '').trim();
  return text ? text.slice(0, CLARIFY_LABEL_MAX) : byKey;
};

/** 칩 표시용 라벨. 긴 질문 라벨은 말줄임(전문은 title 로). */
export const clarifyChipLabel = (label) => {
  const text = String(label || '').trim();
  if (text.length <= CLARIFY_CHIP_LABEL_MAX) return text;
  const head = text.slice(0, CLARIFY_CHIP_LABEL_MAX);
  const wordEnd = head.lastIndexOf(' ');
  return `${(wordEnd >= CLARIFY_CHIP_LABEL_MAX / 2 ? head.slice(0, wordEnd) : head).trimEnd()}…`;
};

/** [확인한 조건] 메시지 본문을 만든다. 백엔드 parse_clarify_answer 와 같은 형식("라벨: 값 · 라벨: 값"). */
export const buildClarifyAnswerMessage = (answers) => {
  const parts = (answers || [])
    .filter((item) => item && item.value && String(item.value).trim())
    .map((item) => {
      const label = String(item.label || clarifyKeyLabel(item.key))
        .replace(/[:：]/g, '')
        .replace(/\s*·\s*/g, ',')
        .replace(/\s+/g, ' ')
        .trim();
      return `${label}: ${String(item.value).trim().replace(/\s*·\s*/g, ',')}`;
    });
  // 건너뛰기: 백엔드 parse_clarify_answer 가 빈 목록으로 읽는 형식(콜론 없음) → 기본 가정으로 바로 진행
  if (parts.length === 0) return `${CLARIFY_ANSWER_PREFIX} 없음`;
  return `${CLARIFY_ANSWER_PREFIX} ${parts.join(' · ')}`;
};

/** 사용자 메시지가 카드 답변이면 [{label, value}] 로 돌려준다(말풍선 대신 칩으로 그리기 위함). */
export const parseClarifyAnswerMessage = (content) => {
  const text = String(content || '').trim();
  if (!text.startsWith(CLARIFY_ANSWER_PREFIX)) return null;
  const body = text.slice(CLARIFY_ANSWER_PREFIX.length).trim();
  const pairs = body
    .split(/\s+·\s+|\n+/)
    .map((part) => part.trim())
    .filter(Boolean)
    .map((part) => {
      const index = part.search(/[:：]/);
      if (index < 0) return null;
      return { label: part.slice(0, index).trim(), value: part.slice(index + 1).trim() };
    })
    .filter((item) => item && item.label && item.value);
  return pairs;
};

/**
 * auto_resolved 카드의 자동 진행 상한: 같은 사이클에서 이미 한 번 자동 진행했으면 true.
 * messages[index] 가 auto_resolved 카드일 때, 그 앞이 [카드 답변(user)] ← [auto_resolved 카드(assistant)] 패턴이면 상한 도달.
 */
export const isAutoResolveCapReached = (messages, index) => {
  if (!Array.isArray(messages) || index < 2) return false;
  const prevUser = messages[index - 1];
  const prevAssistant = messages[index - 2];
  return Boolean(
    prevUser && prevUser.role === 'user' && String(prevUser.content || '').trim().startsWith(CLARIFY_ANSWER_PREFIX)
    && prevAssistant && prevAssistant.role === 'assistant' && prevAssistant.clarify_card?.auto_resolved,
  );
};

const ClarifyCard = ({
  card,
  result = null,
  isLatest = false,
  busy = false,
  onSubmit,
}) => {
  const questions = useMemo(
    () => (Array.isArray(card?.questions) ? card.questions.slice(0, 3) : []),
    [card],
  );
  const mode = card?.mode === 'B' ? 'B' : 'A';
  const timeoutSeconds = mode === 'A' && Number.isFinite(Number(card?.timeout_seconds)) && Number(card.timeout_seconds) > 0
    ? Number(card.timeout_seconds)
    : 0;
  const active = !result && isLatest && !busy;

  // 선택 상태: key → 값. single 은 추천값으로 시작, text 는 빈 값.
  const [selections, setSelections] = useState(() => {
    const initial = {};
    questions.forEach((q) => {
      if (q.type === 'single') initial[q.key] = q.default || (q.options && q.options[0]) || '';
      else initial[q.key] = '';
    });
    return initial;
  });
  const [customOpen, setCustomOpen] = useState({});
  const [customText, setCustomText] = useState({});
  const [edited, setEdited] = useState(false);

  // 유휴 타이머(A 모드): 카드 안에 포인터/포커스가 있거나 탭이 숨겨지면 멈춘다
  const [elapsedMs, setElapsedMs] = useState(0);
  const pausedRef = useRef(false);
  const hiddenRef = useRef(typeof document !== 'undefined' && document.visibilityState === 'hidden');
  const submittedRef = useRef(false);
  const cardRef = useRef(null);

  const answers = useMemo(() => questions.map((q) => {
    const raw = selections[q.key];
    const value = raw === CUSTOM_VALUE ? (customText[q.key] || '') : (raw || '');
    return { key: q.key, label: clarifyQuestionLabel(q), value: String(value).trim() };
  }), [questions, selections, customText]);

  // 유휴 만료용: "직접 입력"을 열어 두고 비운 문항은 추천값으로 되돌린다(빈 값으로 조건이 빠지지 않게)
  const answersWithDefaults = useMemo(() => answers.map((a, index) => {
    if (a.value) return a;
    const q = questions[index];
    const fallback = q?.type === 'single' ? (q.default || (q.options && q.options[0]) || '') : '';
    return { ...a, value: String(fallback).trim() };
  }), [answers, questions]);

  const submit = useCallback((submitResult) => {
    if (submittedRef.current || !onSubmit) return;
    submittedRef.current = true;
    onSubmit(submitResult === 'timer_expired' ? answersWithDefaults : answers, submitResult);
  }, [answers, answersWithDefaults, onSubmit]);

  // 🔁 auto_resolved: 요청당 카드 1장 규칙 — 이미 카드에 답한 턴에서 모델이 또 물었을 때 서버가 이 플래그를 붙인다.
  //    카드를 보여 주지 않고 추천값으로 즉시 진행한다(사용자는 두 번 질문받지 않는다).
  const autoResolved = Boolean(card?.auto_resolved);
  useEffect(() => {
    if (!autoResolved || !active) return;
    submit('timer_expired');
  }, [autoResolved, active, submit]);

  useEffect(() => {
    if (!active || timeoutSeconds <= 0 || autoResolved) return undefined;
    const onVisibility = () => {
      hiddenRef.current = document.visibilityState === 'hidden';
    };
    document.addEventListener('visibilitychange', onVisibility);
    const interval = window.setInterval(() => {
      if (pausedRef.current || hiddenRef.current) return;
      setElapsedMs((prev) => Math.min(prev + TICK_MS, timeoutSeconds * 1000));
    }, TICK_MS);
    return () => {
      window.clearInterval(interval);
      document.removeEventListener('visibilitychange', onVisibility);
    };
  }, [active, timeoutSeconds, autoResolved]);

  useEffect(() => {
    if (!active || timeoutSeconds <= 0) return;
    if (elapsedMs >= timeoutSeconds * 1000) {
      submit('timer_expired');
    }
  }, [active, elapsedMs, timeoutSeconds, submit]);

  const pause = () => { pausedRef.current = true; };
  const resume = () => { pausedRef.current = false; };
  const resetTimer = () => { setElapsedMs(0); };

  const choose = (question, value) => {
    setEdited(true);
    resetTimer();
    if (value === CUSTOM_VALUE) {
      setCustomOpen((prev) => ({ ...prev, [question.key]: true }));
      setSelections((prev) => ({ ...prev, [question.key]: CUSTOM_VALUE }));
      return;
    }
    setCustomOpen((prev) => ({ ...prev, [question.key]: false }));
    setSelections((prev) => ({ ...prev, [question.key]: value }));
  };

  const handleCustomChange = (question, text) => {
    setEdited(true);
    resetTimer();
    setCustomText((prev) => ({ ...prev, [question.key]: text }));
    setSelections((prev) => ({ ...prev, [question.key]: CUSTOM_VALUE }));
  };

  const handleTextChange = (question, text) => {
    setEdited(true);
    setSelections((prev) => ({ ...prev, [question.key]: text }));
  };

  const canSubmit = mode === 'A'
    ? answers.every((a) => a.value)
    : answers.some((a) => a.value);

  const handleSubmit = () => {
    if (!canSubmit) return;
    submit(edited ? 'edited' : 'answered');
  };

  const handleKeyDown = (event) => {
    if (event.key === 'Enter' && !event.shiftKey && !event.nativeEvent?.isComposing) {
      event.preventDefault();
      handleSubmit();
    }
  };

  // ── 해결된 카드: 한 줄 요약 칩 ──
  if (result) {
    const resolvedAnswers = Array.isArray(result.answers) ? result.answers.filter((a) => a && a.value) : [];
    const label = result.result === 'skipped'
      ? '확인 없이 진행했어요'
      : result.result === 'timer_expired'
        ? '기본값으로 진행했어요'
        : '확인했어요';
    return (
      <div className="clarify-card clarify-card--resolved" data-testid="clarify-card-resolved">
        <span className="clarify-card__resolved-label">{label}</span>
        {resolvedAnswers.length > 0 && (
          <span className="clarify-card__resolved-chips">
            {resolvedAnswers.map((a) => (
              <span className="clarify-card__resolved-chip" key={`${a.key}-${a.value}`} title={a.label || undefined}>
                {clarifyChipLabel(a.label || clarifyKeyLabel(a.key))} <b>{a.value}</b>
              </span>
            ))}
          </span>
        )}
      </div>
    );
  }

  // 자동 처리 카드는 결과가 기록되기 전까지도 화면에 펼치지 않는다
  if (autoResolved) {
    return (
      <div className="clarify-card clarify-card--resolved" data-testid="clarify-card-auto">
        <span className="clarify-card__resolved-label">기본값으로 진행했어요</span>
      </div>
    );
  }

  // 답하지 않고 다음 메시지를 보낸 경우(최신 메시지가 아님) — 접힌 상태로만 남긴다
  if (!isLatest) {
    return (
      <div className="clarify-card clarify-card--resolved" data-testid="clarify-card-stale">
        <span className="clarify-card__resolved-label">확인 없이 진행했어요</span>
      </div>
    );
  }

  const progress = timeoutSeconds > 0 ? Math.min(100, (elapsedMs / (timeoutSeconds * 1000)) * 100) : 0;

  return (
    <div
      className={`clarify-card clarify-card--${mode.toLowerCase()}`}
      data-source={card?.source || 'judge'}
      data-testid="clarify-card"
      onBlur={(event) => { if (!cardRef.current?.contains(event.relatedTarget)) resume(); }}
      onFocus={pause}
      onMouseEnter={pause}
      onMouseLeave={resume}
      onTouchStart={pause}
      ref={cardRef}
      role="group"
      aria-label={card?.title || '확인할 내용'}
    >
      <div className="clarify-card__title">{card?.title || (mode === 'A' ? '몇 가지만 확인할게요' : '이것만 알려주세요')}</div>

      {questions.map((question) => (
        <div className="clarify-card__question" key={question.id || question.key}>
          <div className="clarify-card__question-text" id={`clarify-q-${question.key}`}>{question.text}</div>
          {question.type === 'single' ? (
            <>
              <div aria-labelledby={`clarify-q-${question.key}`} className="clarify-card__options" role="radiogroup">
                {(question.options || []).slice(0, 3).map((option, index) => {
                  const selected = selections[question.key] === option;
                  return (
                    <button
                      aria-checked={selected}
                      className={`clarify-card__chip ${selected ? 'is-selected' : ''}`}
                      disabled={busy}
                      key={option}
                      onClick={() => choose(question, option)}
                      role="radio"
                      type="button"
                    >
                      {option}
                      {index === 0 && option === question.default && (
                        <span className="clarify-card__chip-hint" data-testid="clarify-default-hint">
                          {question.default_source === 'last' ? '지난번' : '추천'}
                        </span>
                      )}
                    </button>
                  );
                })}
                <button
                  aria-checked={selections[question.key] === CUSTOM_VALUE}
                  className={`clarify-card__chip clarify-card__chip--custom ${selections[question.key] === CUSTOM_VALUE ? 'is-selected' : ''}`}
                  disabled={busy}
                  onClick={() => choose(question, CUSTOM_VALUE)}
                  role="radio"
                  type="button"
                >
                  직접 입력
                </button>
              </div>
              {customOpen[question.key] && (
                <input
                  aria-label={`${question.text} 직접 입력`}
                  autoFocus
                  className="clarify-card__input"
                  disabled={busy}
                  maxLength={60}
                  onChange={(event) => handleCustomChange(question, event.target.value)}
                  onKeyDown={handleKeyDown}
                  placeholder="예: 특성화고 3학년"
                  type="text"
                  value={customText[question.key] || ''}
                />
              )}
            </>
          ) : (
            <input
              aria-labelledby={`clarify-q-${question.key}`}
              autoFocus={mode === 'B'}
              className="clarify-card__input"
              disabled={busy}
              maxLength={120}
              onChange={(event) => handleTextChange(question, event.target.value)}
              onKeyDown={handleKeyDown}
              placeholder={question.placeholder || '직접 입력'}
              type="text"
              value={selections[question.key] || ''}
            />
          )}
        </div>
      ))}

      <div className="clarify-card__actions">
        {/* 건너뛰기: 두 모드 모두. 조건 없이 보내면 모델이 스스로 가장 흔한 기본값으로 진행한다 */}
        <button
          className="clarify-card__skip"
          data-testid="clarify-skip"
          disabled={busy}
          onClick={() => submit('skipped')}
          type="button"
        >
          건너뛰기
        </button>
        <button
          className="clarify-card__submit"
          data-testid="clarify-submit"
          disabled={busy || !canSubmit}
          onClick={handleSubmit}
          type="button"
        >
          {card?.submit_label || (mode === 'A' ? '이대로 만들기' : '만들기')}
        </button>
      </div>

      {timeoutSeconds > 0 && (
        <div
          aria-hidden="true"
          className="clarify-card__progress"
          data-testid="clarify-progress"
          title="가만히 두면 추천값으로 진행돼요"
        >
          <div className="clarify-card__progress-bar" style={{ width: `${progress}%` }} />
        </div>
      )}
    </div>
  );
};

export default ClarifyCard;
